import { z } from "zod";
import type { AgreementView } from "./agreement";
import { institutionIdSchema, newPasswordSchema, userIdSchema } from "./auth";
import { BOARD_KEYS, type BoardKey } from "./boards";
import { pageQuerySchema } from "./pagination";
import {
  PLATFORM_INSTITUTION_CODE,
  PLATFORM_PERMISSIONS,
  PLATFORM_POSITIONS,
  type PlatformPermission,
  type Role,
  SCHOOL_ROLES,
  type SchoolRole,
} from "./roles";
import { indianMobileSchema } from "./students";

/**
 * Platform console (Eduleo's own team): school onboarding, documents, contracts, invoices,
 * school control, school sign-in accounts and the platform team. Used by the API and the web
 * app, so both apply the same rules.
 *
 * Money is always whole paise (integers). Dates without a time are "YYYY-MM-DD" strings in
 * India time; timestamps are ISO strings.
 */

// ---------------------------------------------------------------------------------------------
// Shared building blocks
// ---------------------------------------------------------------------------------------------

/** GST state codes (first two digits of a GSTIN), used for addresses and place of supply. */
export const INDIAN_STATES = [
  { code: "01", name: "Jammu and Kashmir" },
  { code: "02", name: "Himachal Pradesh" },
  { code: "03", name: "Punjab" },
  { code: "04", name: "Chandigarh" },
  { code: "05", name: "Uttarakhand" },
  { code: "06", name: "Haryana" },
  { code: "07", name: "Delhi" },
  { code: "08", name: "Rajasthan" },
  { code: "09", name: "Uttar Pradesh" },
  { code: "10", name: "Bihar" },
  { code: "11", name: "Sikkim" },
  { code: "12", name: "Arunachal Pradesh" },
  { code: "13", name: "Nagaland" },
  { code: "14", name: "Manipur" },
  { code: "15", name: "Mizoram" },
  { code: "16", name: "Tripura" },
  { code: "17", name: "Meghalaya" },
  { code: "18", name: "Assam" },
  { code: "19", name: "West Bengal" },
  { code: "20", name: "Jharkhand" },
  { code: "21", name: "Odisha" },
  { code: "22", name: "Chhattisgarh" },
  { code: "23", name: "Madhya Pradesh" },
  { code: "24", name: "Gujarat" },
  { code: "26", name: "Dadra and Nagar Haveli and Daman and Diu" },
  { code: "27", name: "Maharashtra" },
  { code: "29", name: "Karnataka" },
  { code: "30", name: "Goa" },
  { code: "31", name: "Lakshadweep" },
  { code: "32", name: "Kerala" },
  { code: "33", name: "Tamil Nadu" },
  { code: "34", name: "Puducherry" },
  { code: "35", name: "Andaman and Nicobar Islands" },
  { code: "36", name: "Telangana" },
  { code: "37", name: "Andhra Pradesh" },
  { code: "38", name: "Ladakh" },
] as const;

export type StateCode = (typeof INDIAN_STATES)[number]["code"];
const STATE_CODES = INDIAN_STATES.map((state) => state.code) as [StateCode, ...StateCode[]];

export function stateName(code: string): string {
  return INDIAN_STATES.find((state) => state.code === code)?.name ?? code;
}

export const stateCodeSchema = z.enum(STATE_CODES, { error: "Choose a state" });

/**
 * "YYYY-MM-DD" that is a real calendar date. Each step is its own pipe, so a field shows one
 * message at a time without aborting (an aborted issue would stop the form-wide checks).
 */
export const isoDateSchema = z
  .string()
  .trim()
  .min(1, { error: "Enter a date" })
  .pipe(z.string().regex(/^\d{4}-\d{2}-\d{2}$/, { error: "Enter a date like 2026-04-01" }))
  .pipe(
    z.string().refine(
      (value) => {
        const date = new Date(`${value}T00:00:00Z`);
        return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
      },
      { error: "Enter a real date" },
    ),
  )
  .pipe(
    z.string().refine((value) => value >= "1900-01-01" && value <= "2100-12-31", {
      error: "Enter a date between 1900 and 2100",
    }),
  );

/** Whole paise, up to ₹100 crore. */
export const paiseSchema = z
  .number({ error: "Enter an amount" })
  .int({ error: "Enter an amount in whole paise" })
  .min(0, { error: "Amount can't be negative" })
  .max(100_000_000_000, { error: "Amount is too large" });

/** Rupees as typed in a form ("1,25,000" or "999.50"), turned into paise. */
export const rupeesSchema = z
  .string()
  .trim()
  .min(1, { error: "Enter an amount" })
  .transform((value) => value.replace(/[₹,\s]/g, ""))
  .pipe(
    z
      .string()
      .regex(/^\d+(\.\d{1,2})?$/, { error: "Enter an amount like 1250 or 1250.50" })
      .transform((value) => {
        const [whole = "0", fraction = ""] = value.split(".");
        return Number(whole) * 100 + Number(fraction.padEnd(2, "0"));
      })
      .pipe(paiseSchema),
  );

export const optionalText = (max: number, message: string) =>
  z
    .string()
    .trim()
    .max(max, { error: message })
    .transform((value) => (value === "" ? null : value))
    .nullable();

// No `abort` on these minimums: an aborted issue stops the form-wide checks (GSTIN vs state,
// a named "other" board), and every problem should be reported at once. An empty value can
// only fail the minimum anyway.
export const requiredText = (max: number, empty: string, tooLong: string) =>
  z.string().trim().min(1, { error: empty }).max(max, { error: tooLong });

export const gstinSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/, {
    error: "GSTIN has 15 characters, like 29ABCDE1234F1Z5",
  });

export const panSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z]{5}[0-9]{4}[A-Z]$/, { error: "PAN has 10 characters, like ABCDE1234F" });

export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(1, { error: "Enter an email address" })
  .max(254, { error: "Email address is too long" })
  .pipe(z.email({ error: "Enter an email address like name@school.edu.in" }));

/**
 * Runs a cross-field check only when the fields it compares are valid themselves, so it is
 * reported together with every other problem instead of after they are fixed.
 */
function whenValid(...fields: string[]) {
  return (payload: { issues: readonly { path?: readonly PropertyKey[] }[] }) =>
    !payload.issues.some((issue) => fields.includes(String(issue.path?.[0])));
}

/** A field that may be left blank: empty text becomes null, anything else must match `schema`. */
export function blankable(schema: z.ZodType<string, string>) {
  return z
    .string()
    .trim()
    .transform((value) => (value === "" ? null : value))
    .pipe(z.union([z.null(), schema]))
    .nullable();
}

/** Prefixed time-sortable record ID, e.g. "reg_01M6FTAQVFTZXVXX4T71QT3T7H". */
export function recordIdSchema(prefix: string, message: string) {
  return z
    .string()
    .trim()
    .regex(new RegExp(`^${prefix}_[0-9A-HJKMNP-TV-Z]{26}$`), { error: message });
}

export const regionIdSchema = recordIdSchema("reg", "Choose a region");

// ---------------------------------------------------------------------------------------------
// Schools: directory, profile and control
// ---------------------------------------------------------------------------------------------

export const SCHOOL_STATUSES = ["onboarding", "active", "suspended"] as const;
export type SchoolStatus = (typeof SCHOOL_STATUSES)[number];

export const SCHOOL_STATUS_LABELS: Record<SchoolStatus, string> = {
  onboarding: "Onboarding",
  active: "Live",
  suspended: "Suspended",
};

export const PLANS = ["starter", "growth", "scale", "enterprise"] as const;
export type Plan = (typeof PLANS)[number];

export const PLAN_LABELS: Record<Plan, string> = {
  starter: "Starter",
  growth: "Growth",
  scale: "Scale",
  enterprise: "Enterprise",
};

/** A school's board: a key from SCHOOL_BOARDS (boards.ts), or "other" with its name typed in. */
export type Board = BoardKey;

const profileFields = {
  legalName: requiredText(
    160,
    "Enter the registered name of the school or trust",
    "Name is too long",
  ),
  board: z.enum(BOARD_KEYS, { error: "Choose the board" }),
  /** Only for board "other": the board's name as the school writes it. */
  boardOther: optionalText(120, "Board name is too long")
    .optional()
    .transform((value) => value ?? null),
  udiseCode: blankable(z.string().regex(/^\d{11}$/, { error: "UDISE+ code has 11 digits" })),
  affiliationNo: optionalText(40, "Affiliation number is too long"),
  addressLine1: requiredText(160, "Enter the address", "Address is too long"),
  addressLine2: optionalText(160, "Address is too long"),
  city: requiredText(80, "Enter the city or town", "City is too long"),
  district: optionalText(80, "District is too long"),
  stateCode: stateCodeSchema,
  /** Optional: the region (within the school's state) its Regional Manager looks after. */
  regionId: blankable(regionIdSchema)
    .optional()
    .transform((value) => value ?? null),
  pincode: z
    .string()
    .trim()
    .regex(/^[1-9]\d{5}$/, { error: "PIN code has 6 digits" }),
  contactName: requiredText(80, "Enter the contact person's name", "Name is too long"),
  contactDesignation: optionalText(80, "Designation is too long"),
  contactEmail: emailSchema,
  contactPhone: indianMobileSchema,
  billingName: requiredText(160, "Enter the name to put on invoices", "Name is too long"),
  billingAddress: requiredText(400, "Enter the billing address", "Address is too long"),
  billingStateCode: stateCodeSchema,
  billingEmail: emailSchema,
  gstin: blankable(gstinSchema),
  pan: blankable(panSchema),
  expectedStudents: z.coerce
    .number({ error: "Enter the number of students" })
    .int({ error: "Enter a whole number" })
    .min(1, { error: "Enter the number of students" })
    .max(100_000, { error: "That's more students than one school can have" }),
  notes: optionalText(2000, "Notes are too long"),
  /**
   * Where the school is, typed or pasted by the team (Google Maps copies "12.9719, 77.6412" on a
   * right-click). Optional, both or neither, and inside India so swapped numbers are caught.
   */
  latitude: z
    .number({ error: "Enter a number, like 12.9719" })
    .min(6, { error: "That's not in India: latitude is between 6 and 38, like 12.9719" })
    .max(38, { error: "That's not in India: latitude is between 6 and 38, like 12.9719" })
    .nullable()
    .optional()
    .transform((value) => value ?? null),
  longitude: z
    .number({ error: "Enter a number, like 77.6412" })
    .min(68, { error: "That's not in India: longitude is between 68 and 98, like 77.6412" })
    .max(98, { error: "That's not in India: longitude is between 68 and 98, like 77.6412" })
    .nullable()
    .optional()
    .transform((value) => value ?? null),
};

/** A board that isn't listed needs its name. */
function boardNamed(value: { board?: string; boardOther?: string | null }) {
  return value.board !== "other" || Boolean(value.boardOther);
}
const boardNameIssue = {
  error: "Enter the board's name",
  path: ["boardOther"],
  when: whenValid("board", "boardOther"),
};

/** GSTIN starts with its state code; the billing state must match it. */
function gstinMatchesState(value: { gstin?: string | null; billingStateCode?: string }) {
  return !value.gstin || !value.billingStateCode || value.gstin.startsWith(value.billingStateCode);
}
const gstinStateIssue = {
  error: "GSTIN doesn't belong to the billing state",
  path: ["gstin"],
  when: whenValid("gstin", "billingStateCode"),
};

/** A map location needs both numbers (the database refuses half of one). */
type Location = { latitude?: number | null; longitude?: number | null };
const hasLatitude = (value: Location) => value.latitude !== null && value.latitude !== undefined;
const hasLongitude = (value: Location) => value.longitude !== null && value.longitude !== undefined;
const longitudeMissing = {
  error: "Enter the longitude too, or clear the latitude",
  path: ["longitude"],
  when: whenValid("latitude", "longitude"),
};
const latitudeMissing = {
  error: "Enter the latitude too, or clear the longitude",
  path: ["latitude"],
  when: whenValid("latitude", "longitude"),
};

export const schoolProfileSchema = z
  .object(profileFields)
  .refine(gstinMatchesState, gstinStateIssue)
  .refine(boardNamed, boardNameIssue)
  .refine((value) => !hasLatitude(value) || hasLongitude(value), longitudeMissing)
  .refine((value) => !hasLongitude(value) || hasLatitude(value), latitudeMissing);

export const schoolCreateSchema = z
  .object({
    code: z
      .string()
      .trim()
      .min(1, { error: "Choose an Institution ID for the school" })
      .pipe(institutionIdSchema)
      .refine((code) => code !== PLATFORM_INSTITUTION_CODE, {
        error: "This Institution ID is reserved",
      }),
    name: requiredText(160, "Enter the school's name as people know it", "Name is too long").pipe(
      z.string().min(3, { error: "Enter the school's full name" }),
    ),
    plan: z.enum(PLANS, { error: "Choose a plan" }),
    ...profileFields,
  })
  .refine(gstinMatchesState, gstinStateIssue)
  .refine(boardNamed, boardNameIssue)
  .refine((value) => !hasLatitude(value) || hasLongitude(value), longitudeMissing)
  .refine((value) => !hasLongitude(value) || hasLatitude(value), latitudeMissing);

export const schoolProfileUpdateSchema = z
  .object({
    ...profileFields,
    name: schoolCreateSchema.shape.name,
    version: z.number().int().min(1),
  })
  .refine(gstinMatchesState, gstinStateIssue)
  .refine(boardNamed, boardNameIssue)
  .refine((value) => !hasLatitude(value) || hasLongitude(value), longitudeMissing)
  .refine((value) => !hasLongitude(value) || hasLatitude(value), latitudeMissing);

export const schoolListQuerySchema = pageQuerySchema.extend({
  q: z.string().trim().max(80).optional(),
  status: z.enum(SCHOOL_STATUSES).optional(),
  stateCode: stateCodeSchema.optional(),
  regionId: regionIdSchema.optional(),
});

const reasonSchema = requiredText(500, "Enter a reason", "Reason is too long");

export const schoolSuspendSchema = z.object({
  reason: reasonSchema,
  version: z.number().int().min(1),
});
export const schoolReactivateSchema = z.object({ version: z.number().int().min(1) });
export const schoolPlanSchema = z.object({
  plan: z.enum(PLANS, { error: "Choose a plan" }),
  version: z.number().int().min(1),
});

export const accountFieldsSchema = z.object({
  loginId: userIdSchema,
  displayName: requiredText(80, "Enter the person's name", "Name is too long"),
  password: newPasswordSchema,
});

/**
 * Go live: the school's first admin account, created when the checklist is complete. Its User
 * ID is assigned (the school's first number) and returned in SchoolActivationDto. The admin is
 * emailed a one-time link to choose their password; a temporary password is optional, for
 * handing over by phone as well (or instead, when email isn't set up).
 */
export const schoolActivateSchema = z.object({
  admin: z.object({
    displayName: accountFieldsSchema.shape.displayName,
    email: emailSchema,
    password: blankable(newPasswordSchema).default(null),
  }),
  version: z.number().int().min(1),
});

export type SchoolActivate = z.output<typeof schoolActivateSchema>;
export type SchoolCreateInput = z.input<typeof schoolCreateSchema>;
export type SchoolCreate = z.output<typeof schoolCreateSchema>;
export type SchoolProfileInput = z.input<typeof schoolProfileUpdateSchema>;
export type SchoolProfileUpdate = z.output<typeof schoolProfileUpdateSchema>;
export type SchoolListQuery = z.output<typeof schoolListQuerySchema>;

export interface SchoolSummaryDto {
  code: string;
  name: string;
  status: SchoolStatus;
  plan: Plan;
  city: string | null;
  stateCode: string | null;
  regionId: string | null;
  regionName: string | null;
  /** First 3 digits of every User ID in this school, e.g. "482". */
  userIdPrefix: string | null;
  createdAt: string;
  activatedAt: string | null;
}

export interface SchoolProfileDto {
  legalName: string;
  board: Board;
  boardOther: string | null;
  udiseCode: string | null;
  affiliationNo: string | null;
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  district: string | null;
  stateCode: StateCode;
  regionId: string | null;
  pincode: string;
  contactName: string;
  contactDesignation: string | null;
  contactEmail: string;
  contactPhone: string;
  billingName: string;
  billingAddress: string;
  billingStateCode: StateCode;
  billingEmail: string;
  gstin: string | null;
  pan: string | null;
  expectedStudents: number;
  notes: string | null;
  latitude: number | null;
  longitude: number | null;
}

/** One item of the go-live checklist. */
export interface ChecklistItem {
  key: "profile" | DocumentKind | "contract";
  label: string;
  done: boolean;
}

export interface SchoolDetailDto extends SchoolSummaryDto {
  version: number;
  /** When the school's logo was last changed (its version for caching); null without a logo. */
  logoUpdatedAt: string | null;
  statusReason: string | null;
  profile: SchoolProfileDto | null;
  checklist: ChecklistItem[];
  /** True when every checklist item is done, so the school can go live. */
  readyToActivate: boolean;
}

/** Answer to going live: the school, and the first admin's assigned User ID to hand over. */
export interface SchoolActivationDto extends SchoolDetailDto {
  admin: {
    loginId: string;
    displayName: string;
    email: string;
    /** False when the email with the set-password link couldn't be sent; resend it from Sign-in accounts. */
    setupEmailSent: boolean;
  };
}

/** Email a school account a one-time link to choose its own password (support, Sign-in accounts). */
export const accountSetupLinkSchema = z.object({ email: emailSchema });
export type AccountSetupLink = z.output<typeof accountSetupLinkSchema>;

export interface AccountSetupLinkDto {
  sentTo: string;
  expiresAt: string;
}

// ---------------------------------------------------------------------------------------------
// Onboarding documents
// ---------------------------------------------------------------------------------------------

export const DOCUMENT_KINDS = [
  "registration_certificate",
  "affiliation_certificate",
  "pan_card",
  "gst_certificate",
  "signatory_id",
  "signed_contract",
  "other",
] as const;
export type DocumentKind = (typeof DOCUMENT_KINDS)[number];

export const DOCUMENT_KIND_LABELS: Record<DocumentKind, string> = {
  registration_certificate: "Registration or recognition certificate",
  affiliation_certificate: "Board affiliation letter",
  pan_card: "PAN card of the school or trust",
  gst_certificate: "GST registration certificate",
  signatory_id: "ID proof of the authorised signatory",
  signed_contract: "Signed contract",
  other: "Other document",
};

/** Verified copies needed before a school can go live. GST only when a GSTIN is given. */
export const REQUIRED_DOCUMENTS: readonly DocumentKind[] = [
  "registration_certificate",
  "pan_card",
  "signatory_id",
];

export const DOCUMENT_STATUSES = ["pending", "verified", "rejected"] as const;
export type DocumentStatus = (typeof DOCUMENT_STATUSES)[number];

export const DOCUMENT_STATUS_LABELS: Record<DocumentStatus, string> = {
  pending: "Waiting for review",
  verified: "Verified",
  rejected: "Rejected",
};

export const DOCUMENT_MAX_BYTES = 10 * 1024 * 1024;
export const DOCUMENT_CONTENT_TYPES = ["application/pdf", "image/jpeg", "image/png"] as const;
export type DocumentContentType = (typeof DOCUMENT_CONTENT_TYPES)[number];

export const documentKindSchema = z.enum(DOCUMENT_KINDS, { error: "Choose the document type" });

// ---------------------------------------------------------------------------------------------
// School logo: shown in the school's own app (and later on its documents)
// ---------------------------------------------------------------------------------------------

/** Logos are small pictures; 1 MB is plenty for a sharp 1000-pixel PNG. */
export const LOGO_MAX_BYTES = 1024 * 1024;
export const LOGO_CONTENT_TYPES = ["image/png", "image/jpeg"] as const;
export type LogoContentType = (typeof LOGO_CONTENT_TYPES)[number];
/** Shortest and longest side accepted, in pixels: big enough to look sharp, small enough to load. */
export const LOGO_MIN_SIDE = 64;
export const LOGO_MAX_SIDE = 4096;

/** Answer to uploading or removing a logo: when it changed, or null when there is none now. */
export interface SchoolLogoDto {
  logoUpdatedAt: string | null;
}

export const documentReviewSchema = z
  .object({
    decision: z.enum(["verified", "rejected"], { error: "Choose verify or reject" }),
    note: optionalText(500, "Note is too long").optional(),
    version: z.number().int().min(1),
  })
  .refine((value) => value.decision === "verified" || Boolean(value.note), {
    error: "Say why the document is rejected, so the school knows what to send",
    path: ["note"],
  });

export interface SchoolDocumentDto {
  id: string;
  kind: DocumentKind;
  fileName: string;
  contentType: DocumentContentType;
  sizeBytes: number;
  status: DocumentStatus;
  reviewNote: string | null;
  uploadedAt: string;
  uploadedBy: string;
  reviewedAt: string | null;
  reviewedBy: string | null;
  version: number;
}

// ---------------------------------------------------------------------------------------------
// Contracts
// ---------------------------------------------------------------------------------------------

export const CONTRACT_STATUSES = ["draft", "sent", "signed", "terminated"] as const;
export type ContractStatus = (typeof CONTRACT_STATUSES)[number];

export const CONTRACT_STATUS_LABELS: Record<ContractStatus, string> = {
  draft: "Draft",
  sent: "Sent for signature",
  signed: "Signed",
  terminated: "Ended",
};

export const BILLING_CYCLES = ["monthly", "quarterly", "half_yearly", "annual"] as const;
export type BillingCycle = (typeof BILLING_CYCLES)[number];

export const BILLING_CYCLE_LABELS: Record<BillingCycle, string> = {
  monthly: "Monthly",
  quarterly: "Every 3 months",
  half_yearly: "Every 6 months",
  annual: "Yearly",
};

export const BILLING_CYCLE_MONTHS: Record<BillingCycle, number> = {
  monthly: 1,
  quarterly: 3,
  half_yearly: 6,
  annual: 12,
};

const contractFields = {
  plan: z.enum(PLANS, { error: "Choose a plan" }),
  billingCycle: z.enum(BILLING_CYCLES, { error: "Choose how often to bill" }),
  pricePerStudentPaise: paiseSchema.refine((value) => value > 0, {
    error: "Enter the monthly price per student",
  }),
  minBillableStudents: z
    .number({ error: "Enter the minimum number of students" })
    .int({ error: "Enter a whole number" })
    .min(0, { error: "Can't be negative" })
    .max(100_000, { error: "That's more students than one school can have" }),
  setupFeePaise: paiseSchema,
  startDate: isoDateSchema,
  endDate: isoDateSchema,
  paymentTermsDays: z
    .number({ error: "Enter the payment terms" })
    .int({ error: "Enter a whole number of days" })
    .min(0, { error: "Can't be negative" })
    .max(90, { error: "Payment terms can be at most 90 days" }),
  specialTerms: optionalText(4000, "Special terms are too long"),
};

function endsAfterStart(value: { startDate?: string; endDate?: string }) {
  return !value.startDate || !value.endDate || value.endDate > value.startDate;
}
const endIssue = {
  error: "End date must be after the start date",
  path: ["endDate"],
  when: whenValid("startDate", "endDate"),
};

export const contractCreateSchema = z.object(contractFields).refine(endsAfterStart, endIssue);
export const contractUpdateSchema = z
  .object({ ...contractFields, version: z.number().int().min(1) })
  .refine(endsAfterStart, endIssue);

/**
 * Send a contract for electronic signature. The Eduleo person sending signs for Eduleo there
 * and then (they confirm they may); the school's signatory gets an email link, proves it's them
 * with a 6-digit code sent to that address, reads the agreement and signs.
 */
export const contractSendSchema = z.object({
  version: z.number().int().min(1),
  signer: z.object({
    name: signatoryText(80, "Enter who will sign for the school", "Name is too long"),
    designation: signatoryText(80, "Enter their designation", "Designation is too long"),
    email: emailSchema,
  }),
  eduleoDesignation: signatoryText(80, "Enter your designation", "Designation is too long"),
  authorised: z.literal(true, { error: "Confirm that you may sign for Eduleo" }),
  message: optionalText(1000, "Message is too long")
    .optional()
    .transform((value) => value ?? null),
});

export type ContractSend = z.output<typeof contractSendSchema>;
export type ContractSendInput = z.input<typeof contractSendSchema>;

export const SIGNATURE_STATUSES = ["sent", "signed", "declined", "cancelled", "expired"] as const;
export type SignatureStatus = (typeof SIGNATURE_STATUSES)[number];

export const SIGNATURE_STATUS_LABELS: Record<SignatureStatus, string> = {
  sent: "Waiting for the school to sign",
  signed: "Signed",
  declined: "Declined by the school",
  cancelled: "Cancelled",
  expired: "Signing link ran out",
};

/** The latest e-signature request of a contract, as the console shows it. */
/** The agreement text for the console's print view. */
export interface ContractAgreementDto {
  agreement: AgreementView;
  /** True: exactly the text that was sent for signature (and signed, if it was). False: the text
   * as it would be sent now, from the school's current details. */
  asSent: boolean;
  /** Eduleo's contact details for the letterhead, as they are today. */
  letterhead: CompanyContact;
}

export interface ContractSignatureDto {
  status: SignatureStatus;
  signerName: string;
  signerDesignation: string;
  signerEmail: string;
  eduleoSignerName: string;
  eduleoSignerDesignation: string;
  sentAt: string;
  expiresAt: string;
  lastEmailedAt: string;
  /** The signer confirmed the emailed code (first time). */
  verifiedAt: string | null;
  signedAt: string | null;
  declinedAt: string | null;
  declineReason: string | null;
  cancelledAt: string | null;
}

// Signing page (no sign-in: the emailed link is the key, the emailed code proves it's them).

/** What the signing link shows before the code is checked: nothing confidential. */
/**
 * What the signing link shows before the emailed code is entered: anyone holding the link sees
 * this, so no person's name, no terms and only the masked address.
 */
export interface SigningPageDto {
  status: SignatureStatus;
  contractNumber: string;
  schoolName: string;
  supplierName: string;
  /** Masked address the code goes to. */
  sentTo: string;
  expiresAt: string;
}

export interface SigningCodeSentDto {
  sentTo: string;
  expiresIn: number;
}

export const signingCodeSchema = z.object({
  code: z
    .string()
    .trim()
    .transform((value) => value.replace(/\s+/g, ""))
    .pipe(z.string().regex(/^\d{6}$/, { error: "Enter the 6-digit code" })),
});

/** After the code: a short-lived session for reading and signing, and the agreement itself. */
export interface SigningSessionDto {
  session: string;
  expiresIn: number;
  agreement: AgreementView;
  /** Eduleo's contact details for the letterhead, as they are today. */
  letterhead: CompanyContact;
  /** Signed for Eduleo when it was sent. */
  eduleoSigner: { name: string; designation: string; signedAt: string };
  signer: { name: string; designation: string; email: string };
}

const DRAWN_SIGNATURE = /^data:image\/png;base64,[A-Za-z0-9+/]+=*$/;

/**
 * Names and designations printed on the signed agreement. The PDF's fonts only carry Latin
 * letters, so other scripts would print as "?" in the legal record: ask for English letters.
 */
function signatoryText(max: number, empty: string, tooLong: string) {
  return requiredText(max, empty, tooLong).regex(/^[\p{Script=Latin}0-9 .,'’&()/-]+$/u, {
    error: "Use English letters here: the signed PDF can't show other scripts yet",
  });
}

export const signContractSchema = z.object({
  name: signatoryText(80, "Type your full name", "Name is too long"),
  designation: signatoryText(80, "Enter your designation", "Designation is too long"),
  consent: z.literal(true, {
    error: "Tick the box to agree to sign this agreement electronically",
  }),
  signature: z.discriminatedUnion(
    "type",
    [
      z.object({ type: z.literal("typed") }),
      z.object({
        type: z.literal("drawn"),
        image: z
          .string()
          .max(400_000, { error: "That drawing is too big. Clear it and draw again." })
          .regex(DRAWN_SIGNATURE, { error: "Draw your signature again" }),
      }),
    ],
    { error: "Type or draw your signature" },
  ),
});

export type SignContract = z.output<typeof signContractSchema>;

export const declineContractSchema = z.object({
  reason: requiredText(500, "Tell Eduleo why you won't sign", "Reason is too long"),
});

export interface SignedContractDto {
  status: "signed";
  signedAt: string;
}

export const contractSignSchema = z.object({
  schoolSignatoryName: requiredText(80, "Enter who signed for the school", "Name is too long"),
  schoolSignatoryDesignation: requiredText(
    80,
    "Enter their designation",
    "Designation is too long",
  ),
  eduleoSignatoryName: requiredText(80, "Enter who signed for Eduleo", "Name is too long"),
  signedOn: isoDateSchema,
  /** The uploaded scan of the signed contract (a document of kind "signed_contract"). */
  documentId: z.string().regex(/^doc_[0-9A-Z]{26}$/, { error: "Upload the signed contract first" }),
  version: z.number().int().min(1),
});

export const contractTerminateSchema = z.object({
  reason: reasonSchema,
  version: z.number().int().min(1),
});

export type ContractCreateInput = z.input<typeof contractCreateSchema>;
export type ContractCreate = z.output<typeof contractCreateSchema>;
export type ContractUpdate = z.output<typeof contractUpdateSchema>;
export type ContractSign = z.output<typeof contractSignSchema>;

export interface ContractDto {
  id: string;
  number: string;
  schoolCode: string;
  schoolName: string;
  status: ContractStatus;
  plan: Plan;
  billingCycle: BillingCycle;
  pricePerStudentPaise: number;
  minBillableStudents: number;
  setupFeePaise: number;
  startDate: string;
  endDate: string;
  paymentTermsDays: number;
  specialTerms: string | null;
  termsVersion: string;
  /** Eduleo's legal details for the printed agreement; null until configured. */
  supplier: { name: string; address: string; gstin: string } | null;
  schoolSignatoryName: string | null;
  schoolSignatoryDesignation: string | null;
  eduleoSignatoryName: string | null;
  signedOn: string | null;
  signedDocumentId: string | null;
  sentAt: string | null;
  terminatedAt: string | null;
  terminationReason: string | null;
  /** The latest electronic signature request, if it was sent that way. */
  signature: ContractSignatureDto | null;
  version: number;
  createdAt: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------------------------
// Invoices
// ---------------------------------------------------------------------------------------------

export const INVOICE_STATUSES = ["draft", "issued", "paid", "void"] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];

/** What people see: "issued" splits into due, part paid and overdue. */
export type InvoiceDisplayStatus = "draft" | "due" | "part_paid" | "overdue" | "paid" | "void";

export const INVOICE_DISPLAY_STATUS_LABELS: Record<InvoiceDisplayStatus, string> = {
  draft: "Draft",
  due: "Due",
  part_paid: "Part paid",
  overdue: "Overdue",
  paid: "Paid",
  void: "Cancelled",
};

export const PAYMENT_METHODS = ["bank_transfer", "upi", "cheque", "card", "cash", "other"] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  bank_transfer: "Bank transfer (NEFT / RTGS / IMPS)",
  upi: "UPI",
  cheque: "Cheque",
  card: "Card",
  cash: "Cash",
  other: "Other",
};

/** ₹10 crore per line; 50 lines stay far below 2^53 paise. */
export const MAX_LINE_PAISE = 10_000_000_000;

/** SAC for software licensing / SaaS ("licensing services for the right to use software"). */
export const DEFAULT_SAC_CODE = "997331";
/**
 * GST rates allowed on a new line, in basis points (1800 = 18%): the slabs in force since
 * 22 September 2025 (GST 2.0 removed 12% and 28%). Eduleo's software subscriptions are 18%.
 */
export const GST_RATES_BPS = [0, 500, 1800, 4000] as const;
export const DEFAULT_GST_RATE_BPS = 1800;

export const invoiceLineSchema = z
  .object({
    description: requiredText(200, "Describe what this line is for", "Description is too long"),
    sacCode: z
      .string()
      .trim()
      .regex(/^\d{6}$/, { error: "SAC code has 6 digits" }),
    quantity: z
      .number({ error: "Enter the quantity" })
      .int({ error: "Enter a whole number" })
      .min(1, { error: "Quantity must be at least 1" })
      .max(1_000_000, { error: "Quantity is too large" }),
    unitPricePaise: paiseSchema,
    taxRateBps: z
      .number({ error: "Choose the GST rate" })
      .int()
      .refine((value) => (GST_RATES_BPS as readonly number[]).includes(value), {
        error: "Choose a GST rate of 0, 5, 18 or 40%",
      }),
  })
  .refine((line) => line.quantity * line.unitPricePaise <= MAX_LINE_PAISE, {
    // Keeps every total far inside the range JavaScript numbers hold exactly.
    error: "A single line can be at most ₹10 crore",
    path: ["unitPricePaise"],
    when: whenValid("quantity", "unitPricePaise"),
  });

const invoiceFields = {
  issueDate: isoDateSchema,
  dueDate: isoDateSchema,
  periodStart: isoDateSchema.nullable(),
  periodEnd: isoDateSchema.nullable(),
  lines: z
    .array(invoiceLineSchema)
    .min(1, { error: "Add at least one line" })
    .max(50, { error: "An invoice can have at most 50 lines" }),
  notes: optionalText(1000, "Notes are too long"),
};

function datesInOrder(value: {
  issueDate?: string;
  dueDate?: string;
  periodStart?: string | null;
  periodEnd?: string | null;
}) {
  if (value.issueDate && value.dueDate && value.dueDate < value.issueDate) return false;
  return true;
}
function periodInOrder(value: { periodStart?: string | null; periodEnd?: string | null }) {
  if (Boolean(value.periodStart) !== Boolean(value.periodEnd)) return false;
  return !value.periodStart || !value.periodEnd || value.periodEnd >= value.periodStart;
}
const dueIssue = {
  error: "Due date can't be before the invoice date",
  path: ["dueDate"],
  when: whenValid("issueDate", "dueDate"),
};
const periodIssue = {
  error: "Enter both period dates, with the end on or after the start",
  path: ["periodEnd"],
  when: whenValid("periodStart", "periodEnd"),
};

export const invoiceCreateSchema = z
  .object({
    schoolCode: institutionIdSchema,
    contractId: z
      .string()
      .regex(/^con_[0-9A-Z]{26}$/, { error: "Unknown contract" })
      .nullable(),
    ...invoiceFields,
  })
  .refine(datesInOrder, dueIssue)
  .refine(periodInOrder, periodIssue);

export const invoiceUpdateSchema = z
  .object({ ...invoiceFields, version: z.number().int().min(1) })
  .refine(datesInOrder, dueIssue)
  .refine(periodInOrder, periodIssue);

export const invoiceIssueSchema = z.object({ version: z.number().int().min(1) });
export const invoiceVoidSchema = z.object({
  reason: reasonSchema,
  version: z.number().int().min(1),
});

export const paymentCreateSchema = z.object({
  amountPaise: paiseSchema.refine((value) => value > 0, { error: "Enter the amount received" }),
  method: z.enum(PAYMENT_METHODS, { error: "Choose how it was paid" }),
  reference: optionalText(80, "Reference is too long"),
  receivedOn: isoDateSchema,
  note: optionalText(500, "Note is too long"),
});

export const invoiceListQuerySchema = pageQuerySchema.extend({
  status: z.enum(INVOICE_STATUSES).optional(),
  schoolCode: institutionIdSchema.optional(),
});

export type InvoiceLine = z.output<typeof invoiceLineSchema>;
export type InvoiceCreateInput = z.input<typeof invoiceCreateSchema>;
export type InvoiceCreate = z.output<typeof invoiceCreateSchema>;
export type InvoiceUpdate = z.output<typeof invoiceUpdateSchema>;
export type PaymentCreate = z.output<typeof paymentCreateSchema>;
export type InvoiceListQuery = z.output<typeof invoiceListQuerySchema>;

export interface InvoiceLineDto extends InvoiceLine {
  amountPaise: number;
}

export interface PaymentDto {
  id: string;
  amountPaise: number;
  method: PaymentMethod;
  reference: string | null;
  receivedOn: string;
  note: string | null;
  recordedBy: string;
  recordedAt: string;
}

export interface InvoiceTotals {
  subtotalPaise: number;
  cgstPaise: number;
  sgstPaise: number;
  igstPaise: number;
  totalPaise: number;
}

export interface InvoiceSummaryDto extends InvoiceTotals {
  id: string;
  /** Assigned when the invoice is issued; drafts have none. */
  number: string | null;
  schoolCode: string;
  schoolName: string;
  status: InvoiceStatus;
  displayStatus: InvoiceDisplayStatus;
  issueDate: string;
  dueDate: string;
  amountPaidPaise: number;
  balancePaise: number;
}

export interface InvoiceDto extends InvoiceSummaryDto {
  contractId: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  billTo: { name: string; address: string; stateCode: string; gstin: string | null };
  supplier: { name: string; address: string; stateCode: string; gstin: string };
  /**
   * Eduleo's contact details for the letterhead, as they are today. The legal name, address and
   * GSTIN on the letterhead are `supplier`'s, frozen when the invoice was issued.
   */
  letterhead: CompanyContact;
  /** Intra-state supply splits GST into CGST + SGST; inter-state charges IGST. */
  intraState: boolean;
  lines: InvoiceLineDto[];
  payments: PaymentDto[];
  notes: string | null;
  voidReason: string | null;
  issuedAt: string | null;
  version: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * GST on an invoice. Tax is worked out per line and rounded to the paisa; intra-state supply
 * splits it into equal CGST and SGST (any odd paisa goes to SGST), inter-state is all IGST.
 */
export function computeInvoiceTotals(
  lines: readonly Pick<InvoiceLine, "quantity" | "unitPricePaise" | "taxRateBps">[],
  intraState: boolean,
): InvoiceTotals {
  let subtotalPaise = 0;
  let taxPaise = 0;
  for (const line of lines) {
    const amount = line.quantity * line.unitPricePaise;
    subtotalPaise += amount;
    taxPaise += Math.round((amount * line.taxRateBps) / 10_000);
  }
  const cgstPaise = intraState ? Math.floor(taxPaise / 2) : 0;
  const sgstPaise = intraState ? taxPaise - cgstPaise : 0;
  const igstPaise = intraState ? 0 : taxPaise;
  return { subtotalPaise, cgstPaise, sgstPaise, igstPaise, totalPaise: subtotalPaise + taxPaise };
}

/** Status people see, from the stored status, payments and today's date (India time). */
export function invoiceDisplayStatus(
  invoice: { status: InvoiceStatus; dueDate: string; amountPaidPaise: number },
  today: string,
): InvoiceDisplayStatus {
  if (invoice.status !== "issued") return invoice.status;
  if (invoice.dueDate < today) return "overdue";
  return invoice.amountPaidPaise > 0 ? "part_paid" : "due";
}

/** Indian financial year of a date, e.g. "2026-05-10" → "2026-27". */
export function financialYearOf(isoDate: string): string {
  const year = Number(isoDate.slice(0, 4));
  const month = Number(isoDate.slice(5, 7));
  const start = month >= 4 ? year : year - 1;
  return `${start}-${String((start + 1) % 100).padStart(2, "0")}`;
}

/** Today's date in India ("YYYY-MM-DD"), whatever the server's time zone. */
export function indiaToday(now: Date): string {
  return new Date(now.getTime() + 330 * 60_000).toISOString().slice(0, 10);
}

/** ₹1,25,000.50 style, Indian digit grouping. */
export function formatPaise(paise: number): string {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(paise / 100);
}

const ONES = [
  "",
  "One",
  "Two",
  "Three",
  "Four",
  "Five",
  "Six",
  "Seven",
  "Eight",
  "Nine",
  "Ten",
  "Eleven",
  "Twelve",
  "Thirteen",
  "Fourteen",
  "Fifteen",
  "Sixteen",
  "Seventeen",
  "Eighteen",
  "Nineteen",
];
const TENS = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

function belowHundred(value: number): string {
  if (value < 20) return ONES[value] ?? "";
  const ones = value % 10;
  return `${TENS[Math.floor(value / 10)]}${ones ? ` ${ONES[ones]}` : ""}`;
}

function belowThousand(value: number): string {
  const hundreds = Math.floor(value / 100);
  const rest = value % 100;
  return [hundreds ? `${ONES[hundreds]} Hundred` : "", rest ? belowHundred(rest) : ""]
    .filter(Boolean)
    .join(" ");
}

function wholeNumberInWords(value: number): string {
  if (value === 0) return "Zero";
  const crore = Math.floor(value / 10_000_000);
  const lakh = Math.floor((value % 10_000_000) / 100_000);
  const thousand = Math.floor((value % 100_000) / 1000);
  const rest = value % 1000;
  return [
    crore ? `${wholeNumberInWords(crore)} Crore` : "",
    lakh ? `${belowHundred(lakh)} Lakh` : "",
    thousand ? `${belowHundred(thousand)} Thousand` : "",
    rest ? belowThousand(rest) : "",
  ]
    .filter(Boolean)
    .join(" ");
}

/** Amount in words for invoices, Indian style: "Rupees One Lakh Twenty Thousand and Fifty Paise Only". */
export function amountInWords(paise: number): string {
  const rupees = Math.floor(paise / 100);
  const remainder = paise % 100;
  const paiseText = remainder ? ` and ${belowHundred(remainder)} Paise` : "";
  return `Rupees ${wholeNumberInWords(rupees)}${paiseText} Only`;
}

// ---------------------------------------------------------------------------------------------
// Sign-in accounts: a school's users (support) and the platform team
// ---------------------------------------------------------------------------------------------

export const ACCOUNT_STATUSES = ["active", "disabled"] as const;
export type AccountStatus = (typeof ACCOUNT_STATUSES)[number];

/** A non-empty set of roles from `roles`, de-duplicated and sorted. */
function roleList<T extends Role>(roles: readonly [T, ...T[]], empty: string) {
  return z
    .array(z.enum(roles, { error: "Unknown role" }))
    .min(1, { error: empty })
    .max(roles.length)
    .transform((values) => [...new Set(values)].sort());
}

const schoolRoles = () => roleList<SchoolRole>(SCHOOL_ROLES, "Choose at least one role");

/** A school account: the User ID is the school's next number, assigned by Eduleo. */
export const schoolAccountCreateSchema = accountFieldsSchema
  .omit({ loginId: true })
  .extend({ roles: schoolRoles() });

export const schoolAccountUpdateSchema = z.object({
  displayName: accountFieldsSchema.shape.displayName.optional(),
  roles: schoolRoles().optional(),
  status: z.enum(ACCOUNT_STATUSES).optional(),
  version: z.number().int().min(1),
});

/** Most states, regions or schools one team member's area can list. Keeps tokens small. */
export const MAX_AREA_ITEMS = 200;

const sortedUnique = <T extends string>(values: readonly T[]) => [...new Set(values)].sort();
const areaList = <T extends string>(item: z.ZodType<T, string>, tooMany: string) =>
  z.array(item).max(MAX_AREA_ITEMS, { error: tooMany }).default([]).transform(sortedUnique);

/** The schools a team member covers. Which lists count depends on their position. */
export const teamAreaSchema = z.object({
  states: areaList(stateCodeSchema, "Choose fewer states"),
  regionIds: areaList(regionIdSchema, "Choose fewer regions"),
  schoolCodes: areaList(institutionIdSchema, "Choose fewer schools"),
  excludedSchoolCodes: areaList(institutionIdSchema, "Exclude fewer schools"),
});

export type TeamArea = z.output<typeof teamAreaSchema>;

export const TEAM_POSITIONS = ["platform_admin", ...PLATFORM_POSITIONS] as const;
export type TeamPosition = (typeof TEAM_POSITIONS)[number];

const EMPTY_AREA: TeamArea = {
  states: [],
  regionIds: [],
  schoolCodes: [],
  excludedSchoolCodes: [],
};

/**
 * What a team member does and where: one position, any permissions (none = view only) and the
 * area that position needs. Lists that don't apply to the position are dropped, so a form can
 * keep them while someone switches positions. The super admin covers everything.
 */
export const teamAccessSchema = z
  .object({
    position: z.enum(TEAM_POSITIONS, { error: "Choose a role" }),
    permissions: z
      .array(z.enum(PLATFORM_PERMISSIONS, { error: "Unknown permission" }))
      .max(PLATFORM_PERMISSIONS.length)
      .default([])
      .transform(sortedUnique),
    area: teamAreaSchema.default(EMPTY_AREA),
  })
  .refine((value) => value.position !== "platform_state_manager" || value.area.states.length > 0, {
    error: "Choose at least one state",
    path: ["area", "states"],
  })
  .refine(
    (value) => value.position !== "platform_regional_manager" || value.area.regionIds.length > 0,
    { error: "Choose at least one region", path: ["area", "regionIds"] },
  )
  .refine(
    (value) => value.position !== "platform_school_manager" || value.area.schoolCodes.length > 0,
    { error: "Choose at least one school", path: ["area", "schoolCodes"] },
  )
  .transform(({ position, permissions, area }) => {
    if (position === "platform_admin") {
      return { position, permissions: [] as PlatformPermission[], area: EMPTY_AREA };
    }
    const excluded = position === "platform_school_manager" ? [] : area.excludedSchoolCodes;
    return {
      position,
      permissions,
      area: {
        states: position === "platform_state_manager" ? area.states : [],
        regionIds: position === "platform_regional_manager" ? area.regionIds : [],
        schoolCodes: position === "platform_school_manager" ? area.schoolCodes : [],
        excludedSchoolCodes: excluded,
      },
    };
  });

export type TeamAccess = z.output<typeof teamAccessSchema>;
export type TeamAccessInput = z.input<typeof teamAccessSchema>;

/** Stored roles for a team member: the position plus their permissions. */
export function teamRoles(access: Pick<TeamAccess, "position" | "permissions">): Role[] {
  return access.position === "platform_admin"
    ? ["platform_admin"]
    : [access.position, ...access.permissions];
}

export const teamMemberCreateSchema = accountFieldsSchema.extend({ access: teamAccessSchema });

export const teamMemberUpdateSchema = z.object({
  displayName: accountFieldsSchema.shape.displayName.optional(),
  access: teamAccessSchema.optional(),
  status: z.enum(ACCOUNT_STATUSES).optional(),
  version: z.number().int().min(1),
});

export const teamMfaResetSchema = z.object({ version: z.number().int().min(1) });

export const passwordResetSchema = z.object({ password: newPasswordSchema });

export const accountListQuerySchema = pageQuerySchema.extend({
  q: z.string().trim().max(80).optional(),
});

export type SchoolAccountCreate = z.output<typeof schoolAccountCreateSchema>;
export type SchoolAccountUpdate = z.output<typeof schoolAccountUpdateSchema>;
export type TeamMemberCreate = z.output<typeof teamMemberCreateSchema>;
export type TeamMemberCreateInput = z.input<typeof teamMemberCreateSchema>;
export type TeamMemberUpdate = z.output<typeof teamMemberUpdateSchema>;
export type AccountListQuery = z.output<typeof accountListQuerySchema>;

/** A sign-in account as support sees it: who, what roles, whether it works. No personal records. */
export interface AccountDto {
  id: string;
  loginId: string;
  displayName: string;
  roles: Role[];
  status: AccountStatus;
  lastLoginAt: string | null;
  /** Still on a temporary password someone else set; they choose their own at next sign-in. */
  mustChangePassword: boolean;
  version: number;
  createdAt: string;
}

/** A team member's area with names, for showing and editing. */
export interface TeamAreaDto {
  states: StateCode[];
  regions: { id: string; name: string; stateCode: StateCode }[];
  schools: { code: string; name: string }[];
  excludedSchools: { code: string; name: string }[];
}

export type MfaMethod = "totp" | "email";

/** A member of Eduleo's own team, as the Team screen shows them. */
export interface TeamMemberDto extends AccountDto {
  /** Null only for an old account that predates positions: it covers no schools until edited. */
  position: TeamPosition | null;
  permissions: PlatformPermission[];
  area: TeamAreaDto;
  /**
   * True when the viewer can't manage the team: then `area.schools` and `area.excludedSchools`
   * are left empty, so nobody learns of schools outside their own area from the team list.
   */
  areaSchoolsHidden: boolean;
  mfa: { enabled: boolean; method: MfaMethod | null };
}

// ---------------------------------------------------------------------------------------------
// Your own profile (System settings): what a team member may change about themselves
// ---------------------------------------------------------------------------------------------

/**
 * Your name as the team sees it, and the work email where Eduleo sends your notifications.
 * Your role, permissions and area are changed only by a super admin (Team page).
 */
export const profileUpdateSchema = z.object({
  displayName: requiredText(80, "Enter your name", "Name is too long"),
  email: blankable(emailSchema).default(null),
  version: z.number().int().min(1),
});

export type ProfileInput = z.input<typeof profileUpdateSchema>;
export type ProfileUpdate = z.output<typeof profileUpdateSchema>;

/** The signed-in team member, as their Profile settings show them. */
export interface ProfileDto {
  id: string;
  loginId: string;
  displayName: string;
  /** Work email for notifications; null until they add one. */
  email: string | null;
  /** Null only for an old account that predates positions. */
  position: TeamPosition | null;
  permissions: PlatformPermission[];
  /** Their own area, in full (it's theirs to know). */
  area: TeamAreaDto;
  mfa: { enabled: boolean; method: MfaMethod | null };
  lastLoginAt: string | null;
  createdAt: string;
  version: number;
}

// ---------------------------------------------------------------------------------------------
// Regions: groups of schools within one state, looked after by Regional Managers
// ---------------------------------------------------------------------------------------------

export const regionCreateSchema = z.object({
  name: requiredText(80, "Enter the region's name", "Name is too long"),
  stateCode: stateCodeSchema,
});

export const regionUpdateSchema = z.object({
  name: regionCreateSchema.shape.name,
  version: z.number().int().min(1),
});

export type RegionCreate = z.output<typeof regionCreateSchema>;
export type RegionUpdate = z.output<typeof regionUpdateSchema>;

export interface RegionDto {
  id: string;
  name: string;
  stateCode: StateCode;
  /** Schools placed in this region, and team members whose area names it. */
  schools: number;
  teamMembers: number;
  version: number;
  createdAt: string;
}

// ---------------------------------------------------------------------------------------------
// Company details: Eduleo's own legal details, printed on every contract and GST invoice
// ---------------------------------------------------------------------------------------------

/** Eduleo's GSTIN is registered in the state of its registered office. */
const companyGstinIssue = {
  error: "GSTIN doesn't belong to this state. Its first 2 digits are the state's code.",
  path: ["gstin"],
  when: whenValid("gstin", "stateCode"),
};

/**
 * Saving Eduleo's company details (create or replace). `version` is the one the details were
 * read at: 0 while nothing is saved yet (the setup values or none), so two first saves can't
 * both win.
 */
/**
 * Corporate Identity Number (21 characters) of a company, or the LLP Identification Number
 * (AAA-1234) of a limited liability partnership.
 */
export const cinSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^(?:[LU][0-9]{5}[A-Z]{2}[0-9]{4}[A-Z]{3}[0-9]{6}|[A-Z]{3}-[0-9]{4})$/, {
    error: "CIN has 21 characters, like U72900KA2024PTC123456 (an LLPIN looks like AAB-1234)",
  });

/** An office phone number as it should be printed: a landline or a mobile. */
export const officePhoneSchema = z
  .string()
  .trim()
  .max(20, { error: "Phone number is too long" })
  .regex(/^\+?[0-9][0-9 ()-]{6,17}[0-9]$/, {
    error: "Enter a phone number, like +91 80 4567 8900",
  });

/** A website as people type it ("eduleo.in", "https://www.eduleo.in"); stored as typed. */
export const websiteSchema = z
  .string()
  .trim()
  .max(120, { error: "Website address is too long" })
  .regex(/^(https?:\/\/)?([a-z0-9]([a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}(\/[^\s]*)?$/i, {
    error: "Enter a website address, like www.eduleo.in",
  });

/** Contact lines are optional; old clients that don't send them clear nothing they never had. */
const optionalContact = (schema: z.ZodType<string, string>) => blankable(schema).default(null);

export const companyProfileSchema = z
  .object({
    legalName: requiredText(160, "Enter Eduleo's registered company name", "Name is too long"),
    address: requiredText(300, "Enter the registered office address", "Address is too long"),
    stateCode: stateCodeSchema,
    gstin: z.string().trim().min(1, { error: "Enter the GSTIN" }).pipe(gstinSchema),
    cin: optionalContact(cinSchema),
    email: optionalContact(emailSchema),
    phone: optionalContact(officePhoneSchema),
    website: optionalContact(websiteSchema),
    version: z.number().int().min(0),
  })
  .refine((value) => value.gstin.startsWith(value.stateCode), companyGstinIssue);

export type CompanyProfileInput = z.input<typeof companyProfileSchema>;
export type CompanyProfileUpdate = z.output<typeof companyProfileSchema>;

/**
 * Where the details in use come from: saved on the Company details page, the values given when
 * Eduleo OS was set up (`SUPPLIER_*` settings), or nowhere yet.
 */
export const COMPANY_SOURCES = ["saved", "setup", "none"] as const;
export type CompanySource = (typeof COMPANY_SOURCES)[number];

/**
 * Eduleo's contact lines on the letterhead of every document it generates. The Companies Act
 * (section 12) asks for the CIN, and the phone, email and website if there are any, on a
 * company's letters and bills. Empty until someone adds them.
 */
export interface CompanyContact {
  cin: string | null;
  email: string | null;
  phone: string | null;
  website: string | null;
}

export const NO_COMPANY_CONTACT: CompanyContact = {
  cin: null,
  email: null,
  phone: null,
  website: null,
};

export interface CompanyDetails extends CompanyContact {
  legalName: string;
  /** Registered office; may span several lines. */
  address: string;
  stateCode: StateCode;
  gstin: string;
}

export interface CompanyProfileDto {
  source: CompanySource;
  /** The details printed on new contracts and invoices; null when there are none yet. */
  details: CompanyDetails | null;
  /** The saved version; 0 until details are saved here. Send it back when saving. */
  version: number;
  /** When and by whom they were last saved here; null unless `source` is "saved". */
  updatedAt: string | null;
  updatedBy: string | null;
}

// ---------------------------------------------------------------------------------------------
// Overview
// ---------------------------------------------------------------------------------------------

export interface PlatformOverviewDto {
  schools: Record<SchoolStatus, number>;
  /** Null when the person can't see billing. */
  billing: {
    outstandingPaise: number;
    overduePaise: number;
    overdueInvoices: number;
    draftInvoices: number;
  } | null;
}

/** What the signed-in platform person may do, so the console can hide controls. The API still checks. */
export interface PlatformCapabilities {
  /** onboard: may add schools; edit: may change details and logo of at least one school. */
  schools: { view: boolean; onboard: boolean; edit: boolean; control: boolean };
  documents: { view: boolean; manage: boolean };
  contracts: { view: boolean; manage: boolean };
  billing: { view: boolean; manage: boolean };
  /** manageAdmins: may add or reset school admins and principals (super admin only). */
  schoolAccounts: { manage: boolean; manageAdmins: boolean };
  team: { view: boolean; manage: boolean };
  regions: { view: boolean; manage: boolean };
  /** Eduleo's own company details: commercial staff see them, only the super admin edits. */
  company: { view: boolean; manage: boolean };
  /** Address search (Google Places) is set up and the person may add or edit schools. */
  addressSearch: boolean;
}

// ---------------------------------------------------------------------------------------------
// Onboarding helpers: address search and checks before a school is added
// ---------------------------------------------------------------------------------------------

export const placeQuerySchema = z.object({
  q: z.string().trim().min(2, { error: "Type at least 2 letters" }).max(120),
  /** Groups the searches and the final pick of one form into one billed session. */
  session: z.string().regex(/^[A-Za-z0-9_-]{8,64}$/, { error: "Start the search again" }),
});

export interface PlaceSuggestionDto {
  placeId: string;
  /** "Delhi Public School, R.K. Puram" */
  primary: string;
  /** "Sector 12, R.K. Puram, New Delhi, Delhi" */
  secondary: string;
}

/**
 * A picked address, split into the school profile's fields. Any part Google didn't give is null.
 * No coordinates: Google's terms allow keeping Places latitude/longitude for 30 days only, and
 * a school profile is kept for years.
 */
export interface PlaceAddressDto {
  addressLine1: string;
  addressLine2: string | null;
  city: string | null;
  district: string | null;
  stateCode: StateCode | null;
  pincode: string | null;
  formatted: string;
}

/** Before adding a school: is the Institution ID free, and is this school already on Eduleo? */
export interface SchoolCheckDto {
  code: { value: string; available: boolean; suggestions: string[] } | null;
  /** Schools already on Eduleo with the same UDISE+ code. */
  sameUdise: { code: string; name: string; status: SchoolStatus }[];
}

export const schoolCheckQuerySchema = z.object({
  code: z.string().trim().toUpperCase().optional(),
  udise: z
    .string()
    .trim()
    .regex(/^\d{11}$/)
    .optional(),
  name: z.string().trim().max(160).optional(),
  city: z.string().trim().max(80).optional(),
});

/**
 * Institution ID ideas from a school's name and city: initials, then initials-city, e.g.
 * "Delhi Public School", "R K Puram" -> DPS-RKPURAM, DPS-RKP. Always valid Institution IDs.
 */
export function suggestInstitutionIds(name: string, city?: string | null): string[] {
  const words = (text: string, skip: readonly string[]) =>
    text
      .toUpperCase()
      .replace(/'S\b/g, "")
      .replace(/[^A-Z0-9 ]+/g, " ")
      .split(/\s+/)
      .filter((word) => word && !skip.includes(word));
  const nameWords = words(name, ["THE", "OF", "AND", "NO"]);
  const cityWords = words(city ?? "", []);
  const initials = nameWords.map((word) => word[0]).join("");
  // Short names ("Greenfield School") read better as their first word than as two letters.
  const base = initials.length >= 3 ? initials : (nameWords[0]?.slice(0, 10) ?? "SCHOOL");
  const cityShort = cityWords.join("").slice(0, 8);
  const cityInitials = cityWords.map((word) => word[0]).join("");
  const ideas = [
    cityShort ? `${base}-${cityShort}` : "",
    cityInitials.length >= 2 ? `${base}-${cityInitials}` : "",
    base,
  ]
    .map((idea) => idea.slice(0, 32).replace(/-+$/, ""))
    .filter((idea) => /^[A-Z0-9][A-Z0-9-]{2,31}$/.test(idea));
  return [...new Set(ideas)];
}
